<template>
	<div class="manage-deal-box-map">
		<div class="input__wrapper">
			<label :class="data.labelClass">
				<input
					type="text"
					ref="mapInput"
					:id="data.inputId"
					:name="data.name"
					:minlength="data.minlength"
					:placeholder="data.placeholder"
					:class="data.classinput"
					country="us"
					@keydown.enter.prevent
				/>
				<a href="javascript:" class="property-form-box-loc-remove" v-show="activeAddress" @click="resetMap">
					<SvgIcon class="property-form-box-loc-remove__icon" iconName="ico-close-sm"/>
				</a>
			</label>
		</div>
		<PartLoader v-if="renderingInProgress || showLoader" style="left: 50%;z-index: 100;bottom: 50%;"/>
		<GmapMap class="manage-deal-box-map__box" ref="mapRef"
			:center="center"
			:zoom="zoom"
			map-type-id="terrain"
			style="width: 100%; height: 460px; background: transparent !important;"
			:icon="{ url: require('@/assets/images/pin.png')}"
		>
		</GmapMap>
	</div>
</template>

<script>
import Vue from 'vue';
import * as VueGoogleMaps from 'vue2-google-maps';
import { gmapApi } from 'vue2-google-maps';
import SvgIcon from '@/components/base/SvgIcon/SvgIcon';
import PartLoader from '@/components/parts/PartLoader';
import MarkerClusterer from '@googlemaps/markerclustererplus';
import axios from 'axios';

Vue.use(VueGoogleMaps, {
	load: {
		key: process.env.VUE_APP_GOOGLE_MAPS_KEY,
		libraries: 'places'
	},
	installComponents: true
});

export default {
	components: {
		SvgIcon,
		PartLoader
	},

	props: {
		data: {
			type: Object,
			required: true
		},
		details: {
			type: Array,
			required: false
		},
		renderingInProgress: {
			type: Boolean,
			required: false
		}
	},
	data: function () {
		return {
			center: {
				lat: 40.774579546216216,
				lng: -73.38258752306109
			},
			zoom: 9,
			showLoader: false,
			autocomplete: {},
			autocompleteSearch: {},
			resetMap: () => {
			},
			activeAddress: false,
			marker: {},
			MarkerClusterer: null
		};
	},
	computed: {
		google: gmapApi
	},
	mounted: function () {
		this.$refs.mapRef.$mapPromise.then(map => {
			// Create the search box and link it to the UI element.
			const input = this.$refs.mapInput;
			const options = {
				componentRestrictions: { country: 'us' },
				fields: ['place_id', 'address_components', 'geometry', 'icon', 'name', 'formatted_address'],
				center: this.center,
				types: ['address']
			};
			this.autocomplete = new this.google.maps.places.Autocomplete(input, options);
			this.marker = new this.google.maps.Marker({
				map,
				icon: require('@/assets/images/pin.png'),
				size: new this.google.maps.Size(52, 52),
				scaledSize: new this.google.maps.Size(32, 32),
				animation: this.google.maps.Animation.DROP
			});
			this.marker.markerAnimation = this.google.maps.Animation.DROP;

			// Listen for the event fired when the user selects a prediction and retrieve
			// more details for that place.
			this.autocomplete.addListener('place_changed', async () => {
				this.showLoader = true;

				if (this.infowindow) {
					this.infowindow.close();
				}

				this.marker.setVisible(false);

				const place = this.autocomplete.getPlace();
				if (!place?.geometry?.location) {
					return;
				}

				const apiPath = 'https://nominatim.openstreetmap.org/search.php';
				const response = await axios.get(apiPath, {
					params: {
						q: place.formatted_address,
						format: 'json',
						addressdetails: 1
					}
				});
				const address = this.getAddressFromPlace(place, response);
				this.$emit('placeChanged', address);

				map.setCenter(place.geometry.location);
				map.setZoom(17);

				this.marker.setPosition(place.geometry.location);

				this.infowindow = new this.google.maps.InfoWindow({
					content: `<div>
                        <h5 class="info">${place.name}</h5>
						<p class="info-p">${address.fullAddress}</p>
                    </div>`
				});

				this.infowindow.close(map, this.marker);

				this.marker.addListener('click', () => {
					this.infowindow.open(map, this.marker);
				});

				setTimeout(() => {
					this.showLoader = false;
					this.marker.setAnimation(this.google.maps.Animation.DROP);
					this.marker.setVisible(true);
				}, 1000);

				this.google.maps.event.addListener(map, 'click', () => {
					this.infowindow.close(map, this.marker);
				});

				this.resetMap = () => {
					this.showLoader = true;
					this.$refs.mapInput.value = '';
					this.marker.setVisible(false);
					map.setCenter(this.center);
					map.setZoom(this.zoom);
					this.activeAddress = false;
					this.$emit('resetMap');
					this.infowindow.close(map, this.marker);
					setTimeout(() => {
						this.showLoader = false;
					}, 500);
				};
			});
			input.addEventListener('focus', () => {
				this.activeAddress = true;
			});
		});
	},
	methods: {
		getAddressFromPlace: function (place, response) {
			const address = {
				streetNumber: '',
				street: '',
				postcode: '',
				postcodeSuffix: '',
				locality: '',
				area: '',
				neighborhood: '',
				state: '',
				country: '',
				fullAddress: ''
			};

			address.location = {
				lat: place.geometry.location.lat(),
				lng: place.geometry.location.lng()
			};
			for (const component of place.address_components) {
				for (const componentType of component.types) {
					switch (componentType) {
					case 'street_number': {
						address.streetNumber = component.long_name;
						break;
					}

					case 'route': {
						address.street = component.long_name;
						break;
					}

					case 'postal_code': {
						address.postcode = component.long_name;
						break;
					}

					case 'postal_code_suffix': {
						address.postcodeSuffix = component.long_name;
						break;
					}
					case 'locality':
						address.locality = component.long_name;
						break;

					case 'sublocality':
						address.area = component.long_name.toLowerCase();
						break;
					case 'neighborhood':
						address.neighborhood = component.long_name;
						break;

					case 'administrative_area_level_1': {
						address.state = component.short_name;
						break;
					}
					case 'country': {
						address.country = component.long_name;
						break;
					}
					}
				}
			}
			if (!address.neighborhood || address.neighborhood === '') {
				if (response.data.length > 0) {
					address.neighborhood = response.data[0].address.neighbourhood;
				}
			}

			if (address.fullAddress) {
				address.fullAddress = `${address.streetNumber ? address.streetNumber + ', ' : ''}${address.street ? address.street + ', ' : ''}${address.locality ? address.locality + ', ' : ''}${address.state ? address.state : ''}`;
			} else {
				address.fullAddress = `${address.street ? address.street + ', ' : ''}${address.locality ? address.locality + ', ' : ''}${address.state ? address.state : ''}`;
			}
			return address;
		},
		async renderMap(objLocation) {
			this.$emit('toggleRendering', true);
			await this.$refs.mapRef.$mapPromise.then(async (map) => {
				this.showLoader = true;
				this.markerAnimation = true;

				objLocation.center = {
					lat: 40.774579546216216,
					lng: -73.38258752306109
				};

				objLocation.location = {
					lat: objLocation.location.lat,
					lng: objLocation.location.lng
				};

				setTimeout(() => {
					this.showLoader = false;
					this.markerAnimation = false;
					this.$emit('toggleRendering', false);
				}, 500);
				if (objLocation) {
					map.setCenter(objLocation.location);
				} else {
					map.setCenter(objLocation.location);
				}
				map.setZoom(17);
				// this.showLoader = false;
				this.marker.setPosition(objLocation.location);
				this.marker.setVisible(true);
				this.infowindow = new this.google.maps.InfoWindow({
					content: `<div>
							<h5 class="info">${objLocation.street}</h5>
							<p class="info-p">${objLocation.fullAddress}</p>
						</div>`
				});

				this.marker.addListener('click', () => {
					this.infowindow.open(map, this.marker);
				});

				this.google.maps.event.addListener(map, 'click', () => {
					this.infowindow.close(map, this.marker);
				});
			});
		},
		renderAllCommercialProperties(objLocation, properties) {
			this.$emit('toggleRendering', true);
			setTimeout(async () => {
				await this.$refs.mapRef.$mapPromise.then(async (map) => {
					if (this.markerClusterer) {
						this.markerClusterer.clearMarkers();
					}
					this.markers = [];
					this.markerClusterer = [];
					const latlnglist = [];
					const bounds = new this.google.maps.LatLngBounds(null);

					objLocation.forEach(element => {
						let obj;
						if (element.location) {
							obj = {
								lat: parseFloat(element.location.lat),
								lng: parseFloat(element.location.lng)
							};
						}
						if (!obj || !obj.lat || !obj.lng) {
							return;
						}
						latlnglist.push(new this.google.maps.LatLng(obj.lat, obj.lng));
						const marker = new this.google.maps.Marker({
							position: obj,
							icon: require('@/assets/images/pin.png'),
							optimized: true
						});
						this.markers.push(marker);
						let propertyAddress = '';
						const elementAddress = (element.streetNumber ? element.streetNumber + ' ' : '') + element.street;
						if (properties) {
							propertyAddress = properties?.find(property => property?.title?.indexOf(elementAddress) > -1);
							let infowindow = '';
							const agent = JSON.parse(localStorage.getItem('user'));
							if (!agent) {
								if (propertyAddress) {
									infowindow = new this.google.maps.InfoWindow({
										content: `<div>
											<a href="/admin/properties/single/${propertyAddress.id}"><h5 class="info">${element.address ? element.address + ' ' : ''}${elementAddress ? elementAddress + ' ' : ''}</h5></a>
										</div>`
									});
								}
							} else {
								if (propertyAddress) {
									infowindow = new this.google.maps.InfoWindow({
										content: `<div>
											<a href="/properties/single/${propertyAddress.id}"><h5 class="info">${element.address ? element.address + ' ' : ''}${elementAddress ? elementAddress + ' ' : ''}</h5></a>
										</div>`
									});
								}
							}

							this.google.maps.event.addListener(marker, 'click', () => {
								infowindow.open(map, marker);
							});
					
							this.google.maps.event.addListener(map, 'click', () => {
								infowindow.close(map, marker);
							});
						}
					});
					for (let i = 0; i < latlnglist.length; i++) {
						bounds.extend(latlnglist[i]);
					}
					setTimeout(async () => {
						this.markerClusterer = new MarkerClusterer(map, this.markers, {
							imagePath:
								'https://developers.google.com/maps/documentation/javascript/examples/markerclusterer/m'
						});
						await map.setCenter(bounds.getCenter());
						let mapWidth = 1256;
						if (window.innerWidth < 1270) {
							mapWidth = window.innerWidth - 10;
						}
						const mapDim = {
							height: 403,
							width: mapWidth
						};
						const getZoom = this.getBoundsZoomLevel(bounds, mapDim);
						await map.setOptions({ minZoom: getZoom, maxZoom: 20 });
						await map.fitBounds(bounds);
						await map.setOptions({ minZoom: 4, maxZoom: 20 });
						this.$emit('toggleRendering', false);
					}, 100);
				});
			}, 200);
		},
		getBoundsZoomLevel(bounds, mapDim) {
			const WORLD_DIM = { height: 300, width: 300 };
			const ZOOM_MAX = 20;

			function latRad(lat) {
				const sin = Math.sin(lat * Math.PI / 180);
				const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
				return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
			}

			function zoom(mapPx, worldPx, fraction) {
				return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
			}

			const ne = bounds.getNorthEast();
			const sw = bounds.getSouthWest();

			const latFraction = (latRad(ne.lat()) - latRad(sw.lat())) / Math.PI;

			const lngDiff = ne.lng() - sw.lng();
			const lngFraction = ((lngDiff < 0) ? (lngDiff + 360) : lngDiff) / 360;

			const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
			const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

			return Math.min(latZoom, lngZoom, ZOOM_MAX);
		}
	}
};
</script>
<style>
.info {
	font-size: 13px;
	margin: 0;
}

.info-p {
	font-size: 12px;
	margin: 0;
}
.vue-map .gm-style {
	background-color: #fff !important;
}

.xxGHyP-dialog-view {
	display: none !important;
}
</style>
<style lang="scss" scoped>
.property-form-box-loc-remove {
	position: absolute;
	color: $fiord;
	right: 20px;
	top: 50%;
	transform: translateY(-50%);
	height: 15px;
	width: 10px;
	font-size: 15px;
	transition: $dur $ease;

	@include hover {
		color: $dodger-blue;
	}
}

.input__wrapper {
	display: block;
	margin-bottom: 0;
	position: absolute;
	z-index: 1;
	top: 5px;
	padding: 16px;
	width: 100%;
	max-width: 100%;
	line-height: 1.2;
	font-size: 14px;
	font-family: "Fira Sans", Arial, Helvetica, sans-serif;
	color: #42526e;
	transition: 350ms ease;
	text-overflow: ellipsis;
	overflow: hidden;
	white-space: nowrap;

	label {
		width: 100%;
		margin-bottom: 0;
		position: relative;
	}

	input {
		display: block;
		box-shadow: none;
		border: 1px solid $white-lilac;
		padding: 14px 16px;
		border-radius: 14px;
		width: 100%;
		max-width: 100%;
		line-height: 1.2;
		font-size: 14px;
		font-family: $font-main;
		color: $fiord;
		transition: $dur $ease;
		text-overflow: ellipsis;
		overflow: hidden;
		white-space: nowrap;
		background-color: $white-lilac;

		@include mq($sm) {
			font-size: 16px;
			padding: 13px 40px 13px 13px;
		}

		&:focus {
			border: 1px solid $dodger-blue;
		}

		@include placeholder {
			color: $grey;
		}
	}

	.property-form__input-title {
		font-family: $font-main;
		font-weight: 500;
		font-size: 14px;
		line-height: 20px;
		color: $white;
		background-color: $fiord;
		padding: 13px 12px;
		display: flex;
		margin-bottom: 18px;
	}

	.admin-property__map {
		margin-bottom: 52px;

		@include mq($xl) {
			margin-bottom: 40px;
		}

		@include mq($lg) {
			margin-bottom: 20px;
		}
	}

	.admin-property__map {
		position: relative;
	}

	.property-form__input-address {
		position: absolute;
		z-index: 1;
		top: 5px;
		max-width: 100%;
		width: 100%;
	}

	.property-form__input-address-form {
		position: relative;
		max-width: 100%;
		padding: 0 20px 0 14px;

		@include mq($xl) {
			padding: 0 15px;
		}
	}
}
</style>
